import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarCheck2,
  CalendarClock,
  Clock,
  ClipboardCheck,
  Clock4,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Receipt,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Wallet,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import CollapsibleCard from '../components/patient/CollapsibleCard'
import { useAuth } from '../contexts/AuthContext'
import { useDashboard } from '../contexts/DashboardContext'
import {
  dashboardMock,
  type AlertActionItem,
  type DashboardKpi,
  type RecentClaimStatus,
} from '../data/dashboardData'

const KPI_ICONS: Record<DashboardKpi['iconKey'], LucideIcon> = {
  billed: Receipt,
  clean_claim: ShieldCheck,
  open_ar: Clock,
  days_to_pay: CalendarCheck2,
  denial_rate: XCircle,
  pa_approval: ClipboardCheck,
}

const KPI_ICON_TONE: Record<DashboardKpi['iconKey'], string> = {
  billed: 'bg-blue-50 text-blue-600',
  clean_claim: 'bg-emerald-50 text-emerald-600',
  open_ar: 'bg-amber-50 text-amber-600',
  days_to_pay: 'bg-teal-50 text-teal-600',
  denial_rate: 'bg-rose-50 text-rose-600',
  pa_approval: 'bg-violet-50 text-violet-600',
}

const ALERT_ICONS: Record<AlertActionItem['iconKey'], LucideIcon> = {
  denied: ShieldAlert,
  eligibility: Clock4,
  prior_auth: ClipboardCheck,
  era: RefreshCw,
  contract: CalendarClock,
}

const ALERT_TONE: Record<AlertActionItem['intent'], string> = {
  critical: 'bg-rose-50 text-rose-600',
  warning: 'bg-amber-50 text-amber-600',
  info: 'bg-blue-50 text-blue-600',
  neutral: 'bg-gray-100 text-gray-600',
}

function formatCurrency(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(value)
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function claimStatusBadge(status: RecentClaimStatus): string {
  if (status === 'Paid') return 'bg-green-100 text-green-700'
  if (status === 'Pending') return 'bg-amber-100 text-amber-700'
  if (status === 'Denied') return 'bg-rose-100 text-rose-700'
  return 'bg-blue-100 text-blue-700'
}

function deltaTone(intent: DashboardKpi['delta']['intent']): string {
  if (intent === 'positive') return 'bg-green-50 text-green-700'
  if (intent === 'negative') return 'bg-rose-50 text-rose-700'
  return 'bg-amber-50 text-amber-700'
}

function KpiCard({ kpi }: { kpi: DashboardKpi }) {
  const Icon = KPI_ICONS[kpi.iconKey]
  const iconClass = KPI_ICON_TONE[kpi.iconKey]
  const DeltaIcon =
    kpi.delta.direction === 'up'
      ? ArrowUpRight
      : kpi.delta.direction === 'down'
        ? ArrowDownRight
        : null

  return (
    <div className="flex-1 min-w-[160px] bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-4 sm:px-5 sm:py-5">
      <div className="flex items-start justify-between gap-2">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconClass}`}
        >
          <Icon size={18} />
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${deltaTone(
            kpi.delta.intent
          )}`}
        >
          {DeltaIcon && <DeltaIcon size={12} strokeWidth={2.5} />}
          {kpi.delta.text}
        </span>
      </div>
      <p className="mt-4 text-2xl sm:text-[1.7rem] font-bold tracking-tight text-gray-900">
        {kpi.value}
      </p>
      <p className="mt-1 text-sm font-medium text-gray-600">{kpi.label}</p>
      <p className="mt-1 text-xs text-gray-500">{kpi.sublabel}</p>
    </div>
  )
}

const ProviderDashboard = () => {
  const { user } = useAuth()
  const { config } = useDashboard()
  const data = useMemo(() => dashboardMock, [])

  const firstName = useMemo(() => {
    const name = user?.displayName ?? user?.username ?? 'Sandra'
    return name.split(/\s+/)[0]
  }, [user])

  const arMax = useMemo(
    () => data.arAging.reduce((max, bucket) => Math.max(max, bucket.amount), 1),
    [data.arAging]
  )

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-start gap-3 min-w-0">
        <span className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <Shield size={18} />
        </span>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            Here&apos;s your billing overview for {data.greetingDate}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {data.kpis.map((kpi) => (
          <KpiCard key={kpi.key} kpi={kpi} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        <div className="space-y-4 sm:space-y-5">
          {config.sectionVisibility.monthlyBilledCollected && (
            <CollapsibleCard
              icon={<LineChartIcon size={16} />}
              iconBgClass="bg-blue-50 text-blue-600"
              title="Monthly billed vs collected"
              subtitle="Last 6 months"
            >
              <div className="mb-3 flex items-center gap-4 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Billed
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Collected
                </span>
              </div>
              <div className="h-56 sm:h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly} barGap={6} barCategoryGap="22%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                    <XAxis
                      dataKey="month"
                      stroke="#94a3b8"
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis hide />
                    <Tooltip
                      cursor={{ fill: 'rgba(15,23,42,0.04)' }}
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                      }}
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Bar dataKey="billed" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Bar
                      dataKey="collected"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CollapsibleCard>
          )}

          {config.sectionVisibility.arAgingBreakdown && (
            <CollapsibleCard
              icon={<Clock size={16} />}
              iconBgClass="bg-amber-50 text-amber-600"
              title="AR aging breakdown"
              subtitle="Outstanding balance by age bucket"
            >
              <ul className="space-y-3">
                {data.arAging.map((bucket) => {
                  const width = Math.max(4, Math.round((bucket.amount / arMax) * 100))
                  return (
                    <li key={bucket.label} className="flex items-center gap-3">
                      <span className="w-20 text-xs text-gray-500 shrink-0">{bucket.label}</span>
                      <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full ${bucket.toneClass}`}
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <span className="w-24 text-right text-sm font-medium text-gray-900 shrink-0">
                        {formatCurrency(bucket.amount)}
                      </span>
                    </li>
                  )
                })}
              </ul>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">Total open AR</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(data.arTotal)}
                </span>
              </div>
            </CollapsibleCard>
          )}
        </div>

        <div className="space-y-4 sm:space-y-5">
          {config.sectionVisibility.alertsActionItems && (
            <CollapsibleCard
              icon={<AlertTriangle size={16} />}
              iconBgClass="bg-amber-50 text-amber-600"
              title="Alerts & action items"
              subtitle="Requires attention today"
            >
              <ul className="divide-y divide-gray-100">
                {data.alerts.map((alert) => {
                  const Icon = ALERT_ICONS[alert.iconKey]
                  return (
                    <li
                      key={alert.id}
                      className="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${ALERT_TONE[alert.intent]}`}
                      >
                        <Icon size={16} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 leading-snug">
                          {alert.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{alert.subtitle}</p>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 text-sm font-semibold text-gray-900 hover:text-primary-700 focus:outline-none focus-visible:underline"
                      >
                        {alert.actionLabel}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </CollapsibleCard>
          )}

          {config.sectionVisibility.payorMixYtd && (
            <CollapsibleCard
              icon={<PieChartIcon size={16} />}
              iconBgClass="bg-emerald-50 text-emerald-600"
              title="Payor mix — YTD"
              subtitle="Revenue share by payor"
            >
              <ul className="space-y-3">
                {data.payorMix.map((slice) => (
                  <li key={slice.label} className="flex items-center gap-3">
                    <span className="flex w-44 items-center gap-2 shrink-0">
                      <span className={`h-2 w-2 rounded-full ${slice.bulletClass}`} />
                      <span className="text-sm text-gray-700 truncate">{slice.label}</span>
                    </span>
                    <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full ${slice.colorClass}`}
                        style={{ width: `${slice.percent}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-sm font-medium text-gray-900 shrink-0">
                      {slice.percent}%
                    </span>
                  </li>
                ))}
              </ul>
            </CollapsibleCard>
          )}

          {config.sectionVisibility.recentClaimActivity && (
            <CollapsibleCard
              icon={<Wallet size={16} />}
              iconBgClass="bg-emerald-50 text-emerald-600"
              title="Recent claim activity"
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Claim
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Patient
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Payor
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Amount
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.recentClaims.map((claim) => (
                      <tr key={claim.id}>
                        <td className="px-2 py-2.5 text-sm font-medium text-gray-900">
                          {claim.claim}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">{claim.patient}</td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">{claim.payor}</td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">
                          {formatCurrency(claim.amount, 2)}
                        </td>
                        <td className="px-2 py-2.5">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${claimStatusBadge(
                              claim.status
                            )}`}
                          >
                            {claim.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleCard>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProviderDashboard
